/** Which extractor runs. OCR_MRZ is deterministic; the other two are models. */
export type ExtractionProvider = 'OCR_MRZ' | 'ANTHROPIC' | 'OPENAI_COMPATIBLE'

export type ProposalDecision = 'PENDING' | 'CONFIRMED' | 'EDITED' | 'REJECTED' | 'FAILED'

export const PROVIDER_LABELS: Record<ExtractionProvider, string> = {
  OCR_MRZ: 'On-device OCR — reads the machine-readable zone',
  ANTHROPIC: 'Anthropic',
  OPENAI_COMPATIBLE: 'OpenAI-compatible endpoint (vLLM, Ollama, or a hosted API)',
}

/**
 * What each provider means for where the document goes.
 *
 * Shown next to the choice, not buried in help text. Choosing a provider is choosing whether
 * a scanned passport leaves the building, and that is the decision being made on this screen.
 */
export const PROVIDER_DATA_NOTE: Record<ExtractionProvider, string> = {
  OCR_MRZ: 'The file never leaves this server.',
  ANTHROPIC: 'The document image is sent to Anthropic.',
  OPENAI_COMPATIBLE: 'The document image is sent to the endpoint you configure below.',
}

/** The tool families the assistant can be given, as the settings screen groups them. */
export type AssistantToolGroup = 'SELF' | 'LOOKUP' | 'PROPOSAL'

/**
 * What each group lets the assistant do, in the words of the decision being made.
 *
 * <p>Written as consequences rather than as feature names: ticking LOOKUP is not "enable
 * lookup tools", it is deciding that somebody who can already open a colleague's profile can
 * also ask about it in prose. The permission still decides who that is.
 */
export const TOOL_GROUP_LABELS: Record<AssistantToolGroup, string> = {
  SELF: 'Their own record',
  LOOKUP: 'Company lookups',
  PROPOSAL: 'Proposing changes',
}

export const TOOL_GROUP_NOTES: Record<AssistantToolGroup, string> = {
  SELF:
    'Leave balance, payslips, attendance, documents, requests, approvals waiting on them, '
    + 'and their direct reports. Every person sees only their own.',
  LOOKUP:
    'The directory, a colleague’s summary, expiring documents, headcount, open positions, '
    + 'holidays and published policies — each still gated on the permission the matching '
    + 'screen requires, so most people can use very little of it.',
  PROPOSAL:
    'Leave, attendance regularisation, an expense claim, a helpdesk ticket, and approving or '
    + 'rejecting something already waiting on them. Nothing is written until the person '
    + 'presses Confirm.',
}

export interface AiSettingsView {
  /** False means never set up, which reads differently from set up and switched off. */
  configured: boolean
  enabled: boolean
  provider: ExtractionProvider
  baseUrl: string | null
  /** The key itself is never returned — only whether one is stored. */
  apiKeySet: boolean
  model: string | null
  allowedDocumentTypes: string[]
  redactTextBeforeCall: boolean
  maxFileBytes: number
  minConfidence: number
  /** Whether the bundled OCR can actually run here. An operational fact, not a setting. */
  ocrAvailable: boolean
  ocrUnavailableReason: string | null

  /** The assistant's half of the same row; both are configured on one screen. */
  assistantEnabled: boolean
  assistantProvider: ExtractionProvider
  assistantModel: string | null
  assistantToolGroups: string[]
  assistantMaxOutputTokens: number
  assistantDataResidencyNote: string | null
  assistantTurnsPerHour: number
}

export interface ProposalView {
  id: string
  provider: ExtractionProvider
  modelVersion: string | null
  storedFileId: string | null
  documentTypeId: string | null
  proposal: Record<string, unknown>
  confidence: Record<string, unknown>
  warnings: string | null
  decision: ProposalDecision
  decidedAt: string | null
  resultingEmployeeDocumentId: string | null
  latencyMs: number | null
}

/** The fields a provider may propose, in the order they are shown. */
export const EXTRACTED_FIELDS = [
  { key: 'documentNumber', label: 'Document number' },
  { key: 'surname', label: 'Surname' },
  { key: 'givenNames', label: 'Given names' },
  { key: 'nationality', label: 'Nationality' },
  { key: 'issuingState', label: 'Issuing state' },
  { key: 'sex', label: 'Sex' },
  { key: 'dateOfBirth', label: 'Date of birth' },
  { key: 'issueDate', label: 'Issue date' },
  { key: 'expiryDate', label: 'Expiry date' },
] as const

export type ExtractedFieldKey = (typeof EXTRACTED_FIELDS)[number]['key']

/**
 * How a confidence reads to a person.
 *
 * The MRZ reader reports 1 or 0 and nothing between — a check digit either verified or it
 * did not — so "certain" here is a stronger claim than a model's 0.97, and the wording says
 * so rather than rendering both as a percentage and letting them look equivalent.
 */
export function confidenceLabel(value: number | null, provider: ExtractionProvider): string {
  if (value == null) return 'not reported'
  if (provider === 'OCR_MRZ') {
    return value >= 1 ? 'check digit verified' : value === 0 ? 'check digit failed' : 'partial read'
  }
  if (value >= 0.9) return `${Math.round(value * 100)}% — high`
  if (value >= 0.7) return `${Math.round(value * 100)}% — worth checking`
  return `${Math.round(value * 100)}% — low, read it yourself`
}

export function confidenceTone(value: number | null): 'good' | 'warn' | 'bad' {
  if (value == null) return 'warn'
  if (value >= 0.9) return 'good'
  if (value >= 0.7) return 'warn'
  return 'bad'
}
